import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ApiAgentService} from '../../../services/api-agent.service';
import {GlobalLoaderService} from '../../../services/global-loader.service';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '../../../services/environment.service';
import {Product} from '../../../models/product';
import {getEditorDefaults, PinturaDefaultImageReaderResult, PinturaDefaultImageWriterResult, PinturaReadState} from '@pqina/pintura';
import {firstValueFrom} from 'rxjs';
import {CouponCode} from '../../../models/coupon-code';
import {CommonModule} from '@angular/common';
import {AngularPinturaModule} from '@pqina/angular-pintura';

declare var Snackbar: any;
declare var $: any;
declare var FileUploadWithPreview: any;

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, AngularPinturaModule, RouterModule],
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit {
  loading: boolean;

  loadingMainPictures: boolean;
  serverProductMainImage: any;
  productMainImageUploader: any;
  productMainPinturaOptions: any;
  productMainUrl: any;
  productMainImageFile: any;

  loadingThumbnailPictures: boolean;
  serverProductThumbnailImage: any;
  productThumbnailImageUploader: any;
  productThumbnailPinturaOptions: any;
  productThumbnailUrl: any;
  productThumbnailImageFile: any;

  loadingGridPictures: boolean;
  serverProductGridImage: any;
  productGridImageUploader: any;
  productGridPinturaOptions: any;
  productGridUrl: any;
  productGridImageFile: any;

  productForm: FormGroup;
  submitted: boolean;
  formStatus: string;
  productId: string;
  productFromServer: Product;

  subAccountType: any;
  subAccountGovId: any;
  isAccountant: boolean;
  isManager: boolean;
  isSales: boolean;
  isMerchant: boolean;
  coupons: CouponCode[];
  couponForm: FormGroup;
  couponFormSubmitted: boolean;
  submittingCoupon: boolean;

  @ViewChild('couponModal') couponModal: any;
  @ViewChild('startDate') startDate: ElementRef;
  @ViewChild('endDate') endDate: ElementRef;

  @ViewChild('mainProductEditor') mainProductEditor?: any;
  @ViewChild('thumbnailProductEditor') thumbnailProductEditor?: any;
  @ViewChild('gridProductEditor') gridProductEditor?: any;

  checkPermission() {
    this.activatedRouter.queryParams.subscribe(params => {
      let perms = this.environmentService.checkPermission(params['type'], params['govId']);
      this.isAccountant = perms.isAccountant;
      this.isSales = perms.isSales;
      this.isManager = perms.isManager;
      this.isMerchant = perms.isMerchant;
    });
  }

  constructor(private activatedRouter: ActivatedRoute,
              private translate: TranslateService,
              private loader: GlobalLoaderService,
              private environmentService: EnvironmentService,
              private http: HttpClient,
              private formBuilder: FormBuilder,
              private router: Router,
              private api: ApiAgentService) {
  }

  ngOnInit() {
    this.productForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      price: ['', [Validators.required, Validators.pattern(/-?\d*\.?\d{1,2}/)]],
      qty: ['', [Validators.required, Validators.pattern(/-?\d*\.?\d{1,2}/)]],
      description: [''],
      digital: [''],
      imageBase64: [''],
    });

    this.couponForm = this.formBuilder.group({
      id: [''],
      couponCode: ['', [Validators.required]],
      discountType: ['', [Validators.required]],
      discountAmount: ['', [Validators.required, this.discountAmountValidator('discountType'), Validators.pattern(/-?\d*\.?\d{1,2}/)]],
      validityPeriod: ['', [Validators.required]],
      startDate: ['', []],
      endDate: ['', []],
      minOrderAmount: ['', []]
    });

    // @ts-ignore
    this.couponForm.get('validityPeriod').valueChanges.subscribe((value) => {
      this.updateDateValidators(value);
    });

    this.couponFormSubmitted = false;
    this.submittingCoupon = false;
    this.loading = true;

    this.activatedRouter.params.subscribe(params => {
      this.formStatus = params['status'];
      this.productId = params['productId'];
      if (this.formStatus == 'edit') {
        // @ts-ignore
        this.productForm.get('title').disable();
      }
      if (this.productId) {
        this.loadProductData().then(() => {
          this.loading = false;
          this.loadResources().then((_) => {
          });
        });
      } else {
        this.loadResources().then((_) => {
          this.loading = false;
        });
      }
      this.checkPermission();
    });
    window.addEventListener('fileUploadWithPreview:imagesAdded', (e) => this.imagesAdded(e));
  }

  async imagesAdded(e: any) {
    let file = e.detail.cachedFileArray.length > 0 ? e.detail.cachedFileArray[0] : null;
    let image64 = await this.fileToBase64(file);
    if (file == null) {
      return;
    }

    // if the upload from the three images inputs, check the size of the image 2MB.
    if (file.size > 2097152) {
      let quality = 2097152 / file.size;
      this.productMainPinturaOptions = this.buildPqinaEditorOptions('main', 1600, 900, quality);
      this.productThumbnailPinturaOptions = this.buildPqinaEditorOptions('thumbnail', 300, 300, quality);
      this.productGridPinturaOptions = this.buildPqinaEditorOptions('grid', 1200, 1200, quality);
    }
    this.productMainUrl = image64;
    this.productThumbnailUrl = image64;
    this.productGridUrl = image64;
  }

  async loadProductData() {
    this.productFromServer = await this.api.loadProduct(this.productId);
    this.productForm.patchValue({
      title: this.productFromServer.title,
      price: this.productFromServer.price,
      qty: this.productFromServer.stockQty,
      digital: this.productFromServer.isDigital,
      description: this.productFromServer.description,
    });

    console.log('product from server', this.productFromServer);
    if (this.productFromServer.discountCoupons) {
      console.log('product coupon from server', this.productFromServer.discountCoupons);
      this.coupons = JSON.parse(this.productFromServer.discountCoupons);
      console.log('coupons', this.coupons);
    }
  }

  private fileToBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  //
  async loadResources() {
    // load external javascript libraries.
    await this.loader.loadExternalStyles('/plugins/notification/snackbar/snackbar.min.css');
    await this.loader.loadExternalStyles('/plugins/file-upload/file-upload-with-preview.min.css');
    await this.loader.loadExternalScript('/plugins/notification/snackbar/snackbar.min.js');
    await this.loader.loadExternalScript('/plugins/file-upload/file-upload-with-preview.min.js');

    await this.loader.loadExternalStyles('https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css');
    await this.loader.loadExternalScript('https://cdn.jsdelivr.net/npm/flatpickr');

    this.checkDateRangeControls();

    console.log('load resources');

    this.productMainPinturaOptions = this.buildPqinaEditorOptions('main', 1600, 900);
    this.productThumbnailPinturaOptions = this.buildPqinaEditorOptions('thumbnail', 300, 300);
    this.productGridPinturaOptions = this.buildPqinaEditorOptions('grid', 1200, 1200);

    // load the main picture from server size.
    if (this.productFromServer && this.productFromServer.productImageId) {
      this.loadingMainPictures = true;
      this.api.loadProductImage(this.productFromServer.id).then(async value => {
        this.serverProductMainImage = value;
        this.productMainUrl = this.serverProductMainImage ? 'data:image/png;base64,' + this.serverProductMainImage : this.environmentService.getRest() + '/openApi/downloadProductImage/' + this.productFromServer.id;
        this.loadingMainPictures = false;
        this.productMainImageUploader = await this.loadImageUploaderWithImage('mainProductImage', this.productMainUrl);
      });
    } else {
      this.productMainUrl = null;
      this.productMainImageUploader = await this.loadImageUploaderWithImage('mainProductImage', './assets/img/no-image.png');
    }

    // load thumbnail picture from the server
    if (this.productFromServer && this.productFromServer.thumbnailImageId) {
      this.loadingThumbnailPictures = true;
      this.api.loadProductThumbnailImage(this.productFromServer.id).then(async value => {
        this.serverProductThumbnailImage = value;
        this.productThumbnailUrl = this.serverProductThumbnailImage ? 'data:image/png;base64,' + this.serverProductThumbnailImage : this.environmentService.getRest() + '/openApi/downloadProductThumbnailImageBytes/' + this.productFromServer.id;
        this.loadingThumbnailPictures = false;
        this.productThumbnailImageUploader = await this.loadImageUploaderWithImage('thumbnailProductImage', this.productThumbnailUrl);
      });
    } else {
      this.productThumbnailUrl = null;
      this.productThumbnailImageUploader = await this.loadImageUploaderWithImage('thumbnailProductImage', './assets/img/no-image.png');
    }

    // load grid picture
    if (this.productFromServer && this.productFromServer.gridImageId) {
      this.loadingGridPictures = true;
      this.api.loadProductGridImage(this.productFromServer.id).then(async value => {
        this.serverProductGridImage = value;
        this.productGridUrl = this.serverProductGridImage ? 'data:image/png;base64,' + this.serverProductGridImage : this.environmentService.getRest() + '/openApi/loadProductGridImage/' + this.productFromServer.gridImageId;
        this.loadingGridPictures = false;
        this.productGridImageUploader = await this.loadImageUploaderWithImage('gridProductImage', this.productGridUrl);
      });
    } else {
      this.productGridUrl = null;
      this.productGridImageUploader = await this.loadImageUploaderWithImage('gridProductImage', './assets/img/no-image.png');
    }
  }

  // @ts-ignore
  get f() {
    return this.productForm.controls;
  }

  showToast(msg: any) {
    Snackbar.show({
      text: msg,
      pos: 'bottom-center'
    });
  }

  async sendToServer(image: any, thumbnailImage: any, gridImage: any) {
    let title = this.productForm.value['title'];
    let price = this.productForm.value['price'];
    let desc = this.productForm.value['description'];
    let digital = this.productForm.value['digital'];
    let qty = this.productForm.value['qty'];
    try {
      if (this.formStatus == 'edit') {
        await this.api.putProduct(this.productFromServer.id, this.productFromServer.title, price, image, thumbnailImage, gridImage, desc, qty, digital, this.coupons);
      } else {
        await this.api.postProduct(title, price, image, thumbnailImage, gridImage, desc, qty, digital, this.coupons);
      }
      this.showToast(await firstValueFrom(this.translate.get('Product saved successfully')));
      this.router.navigate(['/private', 'products']).then();
    } catch (e) {
      console.log('error', e);
      this.showToast(await firstValueFrom(this.translate.get('Could not save the product')));
    } finally {
      this.loading = false;
    }
  }

  private getImageDimension(url: any) {
    return new Promise((resolve) => {
      let img = new Image();
      img.src = url;
      img.addEventListener('load', () => {
        resolve({width: img.width, height: img.height});
      });
    });
  }

  private readFileFromAsset(assetName: any) {
    return new Promise(resolve => {
      this.http.get('./assets/img/' + assetName, {responseType: 'blob'}).subscribe((res => {
            const reader = new FileReader();
            reader.onloadend = () => {
              let base64data = reader.result;
              resolve(base64data);
            };
            reader.readAsDataURL(res);
          }
        )
      );
    });
  }

  async submitForm() {
    this.submitted = true;
    if (this.productMainImageFile == null && this.mainProductEditor != null) {
      console.log('product main image file', this.productMainImageFile);
      await this.mainProductEditor.editor.processImage();
      console.log('product main image file', this.productMainImageFile);
    }

    if (this.productThumbnailImageFile == null && this.thumbnailProductEditor != null) {
      console.log('product thumbnail image file', this.productThumbnailImageFile);
      await this.thumbnailProductEditor.editor.processImage();
      console.log('product thumbnail image file', this.productThumbnailImageFile);
    }

    if (this.productGridImageFile == null && this.gridProductEditor != null) {
      console.log('product grid image file', this.productGridImageFile);
      await this.gridProductEditor.editor.processImage();
      console.log('product grid image file', this.productGridImageFile);
    }

    if (this.productForm.valid) {

      this.loading = true;

      let mainImage64 = null;
      let thumbnailImage64 = null;
      let gridImage64 = null;

      if (this.productMainImageFile) {
        mainImage64 = await this.fileToBase64(this.productMainImageFile);
      }
      if (this.productThumbnailImageFile) {
        thumbnailImage64 = await this.fileToBase64(this.productThumbnailImageFile);
      }
      if (this.productGridImageFile) {
        gridImage64 = await this.fileToBase64(this.productGridImageFile);
      }

      if (!mainImage64 && !this.serverProductMainImage) {
        mainImage64 = await this.readFileFromAsset('no-image-1600x1200.jpg');
      }

      if (!thumbnailImage64 && !this.serverProductThumbnailImage) {
        thumbnailImage64 = await this.readFileFromAsset('no-image-300x300.jpg');
      }

      if (!gridImage64 && !this.serverProductGridImage) {
        gridImage64 = await this.readFileFromAsset('no-image-1200x1200.jpg');
      }

      this.sendToServer(mainImage64, thumbnailImage64, gridImage64).then();

    } else {
      console.log('errors', this.f);
    }
  }

  buildPqinaEditorOptions(productImageState: any, width: any, height: any, quality = 1): any {
    return getEditorDefaults({
      imageWriter: {
        quality: quality,
        targetSize: {
          width: width,
          height: height,
          fit: 'contain',
          upscale: false,
        },
        preprocessImageState: (imageState: any) => {
          // create a new annotation array
          imageState.annotation = imageState.annotation.map((shape: any) => {
            // this is not a text shape so skip
            if (!shape.text) {
              return shape;
            }
            // replace placeholders in text properties
            shape.text = shape.text.replace(/{name}/g, 'John Connor');
            return shape;
          });

          // return updated image state
          return imageState;
        },
        store: (state: any, options: any, onprogress: any) => {
          return new Promise((resolve) => {
            switch (productImageState) {
              case 'main':
                this.productMainImageFile = state.dest;
                break;
              case 'thumbnail':
                this.productThumbnailImageFile = state.dest;
                break;
              case 'grid':
                this.productGridImageFile = state.dest;
                break;
            }
            console.log('state', state);
            console.log('options', options);
            console.log('onprogress', onprogress);
            resolve(state);
          });
        }
      }
    });
  }

  private async loadImageUploaderWithImage(imageUploaderId: any, imageUrl = null) {
    console.log('load image uploader with image');
    // load labels
    let chooseFileLbl = await firstValueFrom(this.translate.get('Choose product image'));
    let browseLbl = await firstValueFrom(this.translate.get('Browse'));
    let imageUploader: any;
    imageUploader = new FileUploadWithPreview(imageUploaderId, {
      text: {
        chooseFile: chooseFileLbl,
        browse: browseLbl
      },
      images: {
        baseImage: imageUrl
      }
    });
    return imageUploader;
  }

  handleEditorProcess($event: PinturaDefaultImageWriterResult) {
    console.log('handle editor process result', $event);
  }

  imageLoadStart($event: void) {
    console.log('image load start', $event);
  }

  imageLoadProgress($event: PinturaReadState) {
    console.log('image load progress', $event);
  }

  imageLoaded($event: PinturaDefaultImageReaderResult) {
    this.mainProductEditor.editor.imageCropAspectRatio = 16 / 9;
    this.thumbnailProductEditor.editor.imageCropAspectRatio = 1;
    this.gridProductEditor.editor.imageCropAspectRatio = 1;
    console.log('image loaded', $event);
  }

  imageLoadPreview($event: ImageData | ImageBitmap) {
    console.log('image load preview', $event);
  }

  addPromotionCoupon() {
    this.couponForm.reset();
    this.couponFormSubmitted = false;
    $(this.couponModal.nativeElement).modal('show');
    setTimeout(() => {
      // @ts-ignore
      this.couponForm.get('validityPeriod').setValue('unlimited');
    }, 200);
  }

  removePromotionCoupon(coupon: CouponCode) {
    this.editPromotionCoupon(coupon);
  }

  editPromotionCoupon(coupon: CouponCode) {
    this.couponForm.reset();
    $(this.couponModal.nativeElement).modal('show');

    console.log('line 473 coupon', coupon);

    setTimeout(() => {
      // fill the form with the coupon data.
      // @ts-ignore
      this.couponForm.get('couponCode').setValue(coupon.couponCode);
      // @ts-ignore
      this.couponForm.get('id').setValue(coupon.couponCode);
      // @ts-ignore
      this.couponForm.get('discountType').setValue(coupon.discountType);
      // @ts-ignore
      this.couponForm.get('discountAmount').setValue(coupon.discountAmount);
      // @ts-ignore
      this.couponForm.get('validityPeriod').setValue(coupon.validityPeriod);
      // @ts-ignore
      this.couponForm.get('startDate').setValue(coupon.startDate);
      // @ts-ignore
      this.couponForm.get('endDate').setValue(coupon.endDate);
      // @ts-ignore
      this.couponForm.get('minOrderAmount').setValue(coupon.minOrderAmount);
    }, 200);
  }

  async deletePromotionCoupon() {
    console.log('delete promotion coupon');

    // Check if the product exists and has an ID
    if (this.productFromServer && this.productFromServer.id) {
      try {
        // Call the API to delete the coupon on the server
        this.coupons = await this.api.deleteCouponCode(this.productId, this.couponForm.value);
      } catch (error) {
        console.error('Error deleting coupon from server:', error);
        // Handle API error (optional: show a message to the user)
      }
    } else {
      // Remove the coupon locally by finding its index and removing it
      const couponCodeToDelete = this.couponForm.value.couponCode;
      const index = this.coupons.findIndex(coupon => coupon.couponCode === couponCodeToDelete);

      if (index !== -1) {
        this.coupons.splice(index, 1); // Remove the coupon from the local list
      } else {
        console.warn('Coupon not found in local list.');
      }
    }

    // Reset the submitting state and close the modal
    this.submittingCoupon = false;
    $(this.couponModal.nativeElement).modal('hide');
  }


  async savePromotionCoupon() {
    // @ts-ignore
    console.log('validity period:', this.couponForm.get('validityPeriod').value);

    // Mark form as submitted
    this.couponFormSubmitted = true;

    if (!this.couponForm.valid) {
      console.warn('Coupon form is invalid.');
      return;
    }

    console.log('coupon form:', this.couponForm.value);
    this.submittingCoupon = true;

    try {
      if (this.productFromServer.id != null) {
        // Save coupon via API for server-synced product
        this.coupons = await this.api.postCouponCode(this.productId, this.couponForm.value);
      } else {
        // Handle local product coupons
        const newCouponCode = this.couponForm.value.couponCode;

        if (!this.coupons) {
          // Initialize the coupon list if it's null
          this.coupons = [this.couponForm.value];
        } else {
          // Check if the coupon already exists
          const existingIndex = this.coupons.findIndex(
            coupon => coupon.couponCode === newCouponCode
          );

          if (existingIndex !== -1) {
            // Update the existing coupon
            this.coupons[existingIndex] = this.couponForm.value;
          } else {
            // Add the new coupon
            this.coupons.push(this.couponForm.value);
          }
        }
      }

      console.log('Updated coupons:', this.coupons);
    } catch (error) {
      console.error('Error saving coupon:', error);
      // Optionally, handle error cases (e.g., show an error message to the user)
    } finally {
      // Reset submitting state and close the modal
      this.submittingCoupon = false;
      this.couponFormSubmitted = false;
      $(this.couponModal.nativeElement).modal('hide');
    }
  }


  generateRandomCode() {
    // generate random code of length 6
    let code = '';
    let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    for (let i = 0; i < 6; i++) {
      code += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    // @ts-ignore
    this.couponForm.get('couponCode').setValue(code);
  }

  checkDateRangeControls() {
    console.log('check date range controls');
    setTimeout(() => {
      if (this.startDate) {
        $(this.startDate.nativeElement).flatpickr({dateFormat: 'Y/m/d'});
      }
      if (this.endDate) {
        $(this.endDate.nativeElement).flatpickr({dateFormat: 'Y/m/d'});
      }
    }, 200);
  }

  private updateDateValidators(validityPeriod: string): void {
    // @ts-ignore
    const startDateControl = this.couponForm.get('startDate');
    // @ts-ignore
    const endDateControl = this.couponForm.get('endDate');

    if (validityPeriod === 'date-range') {
      if (startDateControl) {
        startDateControl.setValidators([Validators.required]);
      }
      if (endDateControl) {
        endDateControl.setValidators([Validators.required, this.dateRangeValidator('startDate')]);
      }
    } else {
      if (startDateControl) {
        startDateControl.clearValidators();
      }
      if (endDateControl) {
        endDateControl.clearValidators();
      }
    }

    if (startDateControl) {
      startDateControl.updateValueAndValidity();
    }
    if (endDateControl) {
      endDateControl.updateValueAndValidity();
    }
  }

  private dateRangeValidator(startDateField: string) {
    return (control: AbstractControl) => {
      const formGroup = control.parent;
      if (!formGroup) {
        return null;
      }
      // @ts-ignore
      const startDate = startDateField ? formGroup.get(startDateField).value : null;
      const endDate = control.value;

      if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
        return {invalidDateRange: true};
      }
      return null;
    };
  }

  private discountAmountValidator(discountTypeField: string) {
    return (control: AbstractControl) => {
      const formGroup = control.parent;

      if (!formGroup) {
        return null;
      }

      // @ts-ignore
      const discountType = formGroup.get(discountTypeField) ? formGroup.get(discountTypeField).value : null;
      const discountAmount = control.value;

      if (discountType && discountAmount && discountType == 'percentage' && discountAmount > 100) {
        return {invalidAmount: true};
      } else if (discountType && discountAmount && discountAmount < 0) {
        return {invalidAmount: true};
      } else {
        // @ts-ignore
        let a = this.productForm.get('price');
        if (discountAmount && discountType && discountType == 'fixed' && discountAmount > (a ? a.value : null)) {
          return {invalidAmount: true};
        }
      }

      return null;
    };
  }
}
